/**
 * Native session-token persistence for the iOS Capacitor shell.
 *
 * Browsers handle auth via httpOnly cookies (`dl_session`). Capacitor's
 * iOS WebView runs from `capacitor://localhost`, so cross-origin cookies
 * to `flythouse.com` are subject to SameSite restrictions and often get
 * dropped. The backend already supports `Authorization: Bearer <token>`
 * as a fallback (`get_current_public_user`), so on iOS we:
 *
 *   1. Capture `session_token` from every auth response
 *   2. Persist it in Capacitor Preferences (encrypted on-device storage)
 *   3. Inject it as a Bearer header on every API call
 *
 * Web build: this module no-ops (cookies stay in charge).
 */
import { Capacitor } from '@capacitor/core';

const STORE_KEY = 'flyt_session_token';
let inMemoryToken = null;

const isNative = () => {
  try { return Capacitor.isNativePlatform(); } catch { return false; }
};

export async function loadStoredToken() {
  if (!isNative()) return null;
  if (inMemoryToken) return inMemoryToken;
  try {
    const { Preferences } = await import('@capacitor/preferences');
    const { value } = await Preferences.get({ key: STORE_KEY });
    inMemoryToken = value || null;
    return inMemoryToken;
  } catch {
    return null;
  }
}

export async function saveToken(token) {
  if (!isNative()) return;
  if (!token) return;
  inMemoryToken = token;
  try {
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.set({ key: STORE_KEY, value: token });
  } catch { /* ignore */ }
}

export async function clearToken() {
  inMemoryToken = null;
  if (!isNative()) return;
  try {
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.remove({ key: STORE_KEY });
  } catch { /* ignore */ }
}

/** Synchronous reader used by the axios interceptor. The token is
 *  pre-loaded at app boot via `loadStoredToken()` so by the time any
 *  API call fires it's already in memory. */
export function getTokenSync() {
  return inMemoryToken;
}


// ── Recent-emails suggestions ────────────────────────────────────
// iter377 · Native iOS + web sign-in autocomplete. Every successful
// login pushes the email onto a small MRU list stored via Capacitor
// Preferences (or `localStorage` on web). The LoginModal reads this
// list and renders tap-chips above the email input so users can pick
// their account with one tap — iOS Passwords autofill then offers
// the matching credential via Face ID once the email is filled.
const RECENT_EMAILS_KEY = 'flyt_recent_emails';
const RECENT_EMAILS_MAX = 3;

async function _readEmails() {
  try {
    if (isNative()) {
      const { Preferences } = await import('@capacitor/preferences');
      const { value } = await Preferences.get({ key: RECENT_EMAILS_KEY });
      return value ? JSON.parse(value) : [];
    }
    const raw = window.localStorage.getItem(RECENT_EMAILS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

async function _writeEmails(list) {
  try {
    const payload = JSON.stringify(list);
    if (isNative()) {
      const { Preferences } = await import('@capacitor/preferences');
      await Preferences.set({ key: RECENT_EMAILS_KEY, value: payload });
      return;
    }
    window.localStorage.setItem(RECENT_EMAILS_KEY, payload);
  } catch { /* ignore */ }
}

/** Push an email onto the MRU list. De-dupes case-insensitively and
 *  keeps the top RECENT_EMAILS_MAX entries. Safe to call on every
 *  login response. */
export async function saveRecentEmail(email) {
  const clean = (email || '').trim().toLowerCase();
  if (!clean || !clean.includes('@')) return;
  const existing = (await _readEmails()).filter((e) => e.toLowerCase() !== clean);
  const next = [clean, ...existing].slice(0, RECENT_EMAILS_MAX);
  await _writeEmails(next);
}

/** Return the MRU email list — chronological, most recent first. */
export async function getRecentEmails() {
  const list = await _readEmails();
  return Array.isArray(list) ? list.filter((e) => typeof e === 'string' && e.includes('@')) : [];
}

/** Wipe recent emails. We DO NOT call this on logout — the whole
 *  point is to surface them again next time. Exposed only for a
 *  "Forget this device" affordance we may add later. */
export async function clearRecentEmails() {
  try {
    if (isNative()) {
      const { Preferences } = await import('@capacitor/preferences');
      await Preferences.remove({ key: RECENT_EMAILS_KEY });
      return;
    }
    window.localStorage.removeItem(RECENT_EMAILS_KEY);
  } catch { /* ignore */ }
}


// ── Avatar-backup persistence ────────────────────────────────────
// iter303 · Native iOS defensive backup for the uploaded profile
// picture. Users reported that after uploading their avatar and
// killing the app, the picture would come back empty on next launch.
// The backend persistence itself is fine (curl-verified round-trip),
// but a rare edge in the native shell — cookie drop mid-upload, HEIC
// canvas quirk, or a killed-mid-flight retry — can leave the server
// without the write we thought we made. To make the flow bullet-proof
// we mirror the last-known-good picture into Capacitor Preferences on
// every successful upload, and on the very next boot the auth context
// hydrates from that mirror + heals the server by re-PUTing. No-op on
// web (nothing to fix there — cookies + normal state persist fine).

const AVATAR_KEY = 'flyt_avatar_backup';
let inMemoryAvatar = null;

/** Read the backup avatar data URL from Preferences (native only). */
export async function loadStoredAvatar() {
  if (!isNative()) return null;
  if (inMemoryAvatar) return inMemoryAvatar;
  try {
    const { Preferences } = await import('@capacitor/preferences');
    const { value } = await Preferences.get({ key: AVATAR_KEY });
    inMemoryAvatar = value || null;
    return inMemoryAvatar;
  } catch {
    return null;
  }
}

/** Persist the successful-upload avatar so we can rehydrate next boot. */
export async function saveAvatar(dataUrl) {
  if (!isNative()) return;
  if (!dataUrl) return;
  inMemoryAvatar = dataUrl;
  try {
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.set({ key: AVATAR_KEY, value: dataUrl });
  } catch { /* ignore */ }
}

/** Wipe the local avatar backup — call after logout so the next user
 *  doesn't inherit the previous user's photo. */
export async function clearAvatar() {
  inMemoryAvatar = null;
  if (!isNative()) return;
  try {
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.remove({ key: AVATAR_KEY });
  } catch { /* ignore */ }
}

/** Synchronous getter for the axios interceptor / render path. */
export function getAvatarSync() {
  return inMemoryAvatar;
}


// ── Agent cover / avatar backup ──────────────────────────────────
// iter329 · Agents reported the same "picture vanishes on relaunch"
// symptom as athletes did back in iter303 — after uploading a FLYT ID™
// cover photo or agency avatar and swiping the native iOS app closed,
// the images came back empty on next boot. Cause is identical: the
// draft PUT to /me/agent/link/draft is fire-and-forget, and if the
// JS runtime suspends mid-flight (Capacitor kills the webview seconds
// after backgrounding) the write never lands on the server.
//
// Fix mirrors the athlete pattern: mirror every successful upload
// into Capacitor Preferences so the next launch can rehydrate the
// image AND fire a heal-PUT that catches the server up. No-op on
// web where cookies + normal state persist fine.

const AGENT_COVER_KEY = 'flyt_agent_cover_backup';
const AGENT_PHOTO_KEY = 'flyt_agent_photo_backup';
let inMemoryAgentCover = null;
let inMemoryAgentPhoto = null;

export async function loadStoredAgentCover() {
  if (!isNative()) return null;
  if (inMemoryAgentCover) return inMemoryAgentCover;
  try {
    const { Preferences } = await import('@capacitor/preferences');
    const { value } = await Preferences.get({ key: AGENT_COVER_KEY });
    inMemoryAgentCover = value || null;
    return inMemoryAgentCover;
  } catch {
    return null;
  }
}

export async function saveAgentCover(dataUrl) {
  if (!isNative()) return;
  if (!dataUrl) return;
  inMemoryAgentCover = dataUrl;
  try {
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.set({ key: AGENT_COVER_KEY, value: dataUrl });
  } catch { /* ignore */ }
}

export async function clearAgentCover() {
  inMemoryAgentCover = null;
  if (!isNative()) return;
  try {
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.remove({ key: AGENT_COVER_KEY });
  } catch { /* ignore */ }
}

export function getAgentCoverSync() {
  return inMemoryAgentCover;
}

export async function loadStoredAgentPhoto() {
  if (!isNative()) return null;
  if (inMemoryAgentPhoto) return inMemoryAgentPhoto;
  try {
    const { Preferences } = await import('@capacitor/preferences');
    const { value } = await Preferences.get({ key: AGENT_PHOTO_KEY });
    inMemoryAgentPhoto = value || null;
    return inMemoryAgentPhoto;
  } catch {
    return null;
  }
}

export async function saveAgentPhoto(dataUrl) {
  if (!isNative()) return;
  if (!dataUrl) return;
  inMemoryAgentPhoto = dataUrl;
  try {
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.set({ key: AGENT_PHOTO_KEY, value: dataUrl });
  } catch { /* ignore */ }
}

export async function clearAgentPhoto() {
  inMemoryAgentPhoto = null;
  if (!isNative()) return;
  try {
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.remove({ key: AGENT_PHOTO_KEY });
  } catch { /* ignore */ }
}

export function getAgentPhotoSync() {
  return inMemoryAgentPhoto;
}


// ── User-snapshot cache ──────────────────────────────────────────
// iter358 · Cold-boot perf win — persist the last-known /me payload
// so we can render the authed UI on the FIRST paint after app launch
// (before /me finishes revalidating over the network). On flaky 4G
// this shaves 400–800ms off "app is blank" time. Data is small (<3KB
// per user), safe to keep across launches, and cleared on logout so
// the next user never inherits stale identity.
const USER_SNAPSHOT_KEY = 'flyt_user_snapshot';
let inMemoryUserSnapshot = null;

// iter445 · Prime the in-memory snapshot SYNCHRONOUSLY at module import
// time. Reading `localStorage` (which WKWebView exposes synchronously on
// native iOS) means the first `useState(getUserSnapshotSync())` call in
// UserAuthContext already sees the last authed user — no async round-trip,
// no logged-out flash before the /me revalidation lands. On native we
// still dual-write to Capacitor Preferences (below in saveUserSnapshot)
// because Preferences survives app upgrades where localStorage sometimes
// gets nuked; localStorage is purely the sync mirror.
try {
  if (typeof window !== 'undefined' && window.localStorage) {
    const raw = window.localStorage.getItem(USER_SNAPSHOT_KEY);
    if (raw) inMemoryUserSnapshot = JSON.parse(raw);
  }
} catch { inMemoryUserSnapshot = null; }

export async function loadStoredUserSnapshot() {
  // Snapshot works on BOTH native and web (localStorage) — most of the
  // perf win is on iOS but web repeat-visits benefit too.
  if (inMemoryUserSnapshot) return inMemoryUserSnapshot;
  try {
    if (isNative()) {
      const { Preferences } = await import('@capacitor/preferences');
      const { value } = await Preferences.get({ key: USER_SNAPSHOT_KEY });
      inMemoryUserSnapshot = value ? JSON.parse(value) : null;
      // iter445 · Backfill the sync mirror so subsequent cold boots
      // read localStorage without waiting on Preferences.
      if (inMemoryUserSnapshot && typeof window !== 'undefined') {
        try { window.localStorage.setItem(USER_SNAPSHOT_KEY, JSON.stringify(inMemoryUserSnapshot)); } catch { /* ignore */ }
      }
    } else if (typeof window !== 'undefined') {
      const raw = window.localStorage.getItem(USER_SNAPSHOT_KEY);
      inMemoryUserSnapshot = raw ? JSON.parse(raw) : null;
    }
  } catch {
    inMemoryUserSnapshot = null;
  }
  return inMemoryUserSnapshot;
}

export async function saveUserSnapshot(user) {
  if (!user || !user.user_id) return;
  inMemoryUserSnapshot = user;
  try {
    const payload = JSON.stringify(user);
    // iter445 · Always write localStorage so the sync mirror is fresh
    // for the next cold boot — on native this runs in parallel with
    // the Preferences.set below.
    if (typeof window !== 'undefined' && window.localStorage) {
      try { window.localStorage.setItem(USER_SNAPSHOT_KEY, payload); } catch { /* ignore quota */ }
    }
    if (isNative()) {
      const { Preferences } = await import('@capacitor/preferences');
      await Preferences.set({ key: USER_SNAPSHOT_KEY, value: payload });
    }
  } catch { /* ignore */ }
}

export async function clearUserSnapshot() {
  inMemoryUserSnapshot = null;
  try {
    // iter445 · Clear BOTH stores on sign-out so the next login on the
    // same device doesn't inherit stale identity from either channel.
    if (typeof window !== 'undefined' && window.localStorage) {
      try { window.localStorage.removeItem(USER_SNAPSHOT_KEY); } catch { /* ignore */ }
    }
    if (isNative()) {
      const { Preferences } = await import('@capacitor/preferences');
      await Preferences.remove({ key: USER_SNAPSHOT_KEY });
    }
  } catch { /* ignore */ }
}

export function getUserSnapshotSync() {
  return inMemoryUserSnapshot;
}
